import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, DropdownMenu, TableListActions, WithInfoIcon, Tooltip, useOverlayContext, useDevice } from 'seagull';
import { ActivityLiner24, CopyLiner24, Download, Edit1Liner24, EmptyWalletTickLiner24, EyeLine, LinkLiner24, PaymentsLine, ProfileLiner24, ProposalLiner24, ReceiptEditLiner24, TrashLiner24 } from 'seagull/icons';
import EnterAccountantAccessButton from '~/components/EnterAccountantAccessButton/EnterAccountantAccessButton';
import { routes } from '~/constants';
import { Intent } from '~/infra/access-control';
import { relationshipModel, relationshipSummaryModel, timeModel } from '~/models';
import { ActivityLogBottomSheetContent } from './ActivityLogBottomSheetContent/ActivityLogBottomSheetContent';
import styles from './AgreementsDashboardRow.module.scss';
import ShareAgreementLinksModal from './ShareAgreementLinksModal';
const agreementStatusToText = {
    [relationshipSummaryModel.AgreementStatus.Active]: 'Active',
    [relationshipSummaryModel.AgreementStatus.Exhausted]: 'Completed',
    [relationshipSummaryModel.AgreementStatus.Terminated]: 'Terminated'
};
export default function AgreementsDashboardRow(props) {
    const { isVendor, index, hasAccountantAccess, onVerifyPaymentMethod, onEditAgreement, onDownloadPDF, onDuplicateAgreement, onTerminateAgreement, onEnterAccountantAccess, onRenameAgreement, onChangeAssignee } = props;
    const [agreement, setAgreement] = useState(props.agreement);
    useEffect(()=>setAgreement(props.agreement), [
        props.agreement
    ]);
    const overlayContext = useOverlayContext();
    const { isMobileDevice } = useDevice();
    const history = useHistory();
    const displayedTitle = relationshipSummaryModel.displayedTitle(agreement, isVendor);
    const testId = agreement.isSample ? 'test-agreement' : "agreements-row-".concat(index);
    const heapId = agreement.isSample ? 'test-agreement' : 'agreement';
    const isTerminated = relationshipSummaryModel.isTerminated(agreement);
    const canCompleteVerification = relationshipSummaryModel.canCompleteVerification(agreement);
    const agreementStatus = relationshipSummaryModel.agreementStatus(agreement);
    const canRequestPM = (!agreement.fundingSource || agreement.fundingSource.failedVerification()) && !relationshipSummaryModel.isTerminated(agreement) && isVendor;
    const agreementLink = isVendor || !relationshipModel.isPending(agreement) ? routes.agreement(agreement.id) : routes.clientOnboarding(agreement.id);
    const items = compact([
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.title,
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        ellipsis: true,
                        hideTooltip: true,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                weight: "normal",
                                testId: "".concat(testId, "-company-name"),
                                tooltipTestId: "".concat(testId, "-company-name-tooltip"),
                                children: relationshipSummaryModel.opposingPartyName(agreement, isVendor)
                            }),
                            displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: "".concat(testId, "-title"),
                                tooltipTestId: "".concat(testId, "-title-tooltip"),
                                children: displayedTitle
                            })
                        ]
                    }),
                    hasAccountantAccess && /*#__PURE__*/ _jsx(EnterAccountantAccessButton, {
                        testId: "".concat(testId, "-enter-acc-access"),
                        onClick: (e)=>{
                            e.stopPropagation(); // Prevents the row <button> element from redirecting to its target.
                            onEnterAccountantAccess(relationshipSummaryModel.customerBusinessId(agreement), relationshipSummaryModel.customerBusinessName(agreement));
                        }
                    })
                ]
            })
        },
        {
            value: /*#__PURE__*/ _jsxs("div", {
                className: styles.status,
                children: [
                    /*#__PURE__*/ _jsx(WithInfoIcon, {
                        showInfo: agreementStatus === relationshipSummaryModel.AgreementStatus.Exhausted,
                        info: "All services in this agreement were completed",
                        infoTestId: "".concat(testId, "-status-info"),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            testId: "".concat(testId, "-status"),
                            children: agreementStatusToText[agreementStatus]
                        })
                    }),
                    agreement.hasAmendments && /*#__PURE__*/ _jsx(Tooltip, {
                        placement: "top",
                        title: "Agreement has changes awaiting approval.",
                        children: /*#__PURE__*/ _jsx(Typography, {
                            testId: "".concat(testId, "-has-amendments"),
                            children: /*#__PURE__*/ _jsx(ReceiptEditLiner24, {
                                size: 16
                            })
                        })
                    })
                ]
            })
        },
        isVendor && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: "".concat(testId, "-assignee"),
                children: relationshipSummaryModel.sellerFullName(agreement)
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                children: agreement.effectiveDate ? timeModel.formatDateLong(agreement.effectiveDate) : ''
            })
        },
        {
            value: !agreement.fundingSource ? /*#__PURE__*/ _jsx(Typography, {
                skin: "error",
                children: "Not set"
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        skin: "secondary",
                        children: [
                            agreement.fundingSource.isCreditCard() ? 'Card' : 'Bank account',
                            " (",
                            agreement.fundingSource.last4(),
                            ")"
                        ]
                    }),
                    agreement.fundingSource.failedVerification() && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "error",
                        children: "Verification failed"
                    }),
                    agreement.fundingSource.isPendingMicroDeposit() && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "warning",
                        children: agreement.fundingSource.isPendingAutomaticVerification() ? 'Automatic verification initiated' : 'Manual verification initiated'
                    })
                ]
            })
        },
        {
            value: agreement.lastBilledDate ? /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: "".concat(testId, "-last-billed"),
                children: timeModel.formatDateLong(agreement.lastBilledDate)
            }) : null
        }
    ]);
    const getButtonText = ()=>{
        if (canCompleteVerification) return 'Complete verification';
        return canRequestPM ? 'Add payment method' : undefined;
    };
    const onButtonClick = ()=>{
        if (canCompleteVerification) {
            onVerifyPaymentMethod(agreement);
        } else {
            history.push(routes.paymentMethods(agreement.id));
        }
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        onClick: ()=>history.push(agreementLink),
        items: items,
        rowId: agreement.id,
        testId: testId,
        "data-heapid": heapId,
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            testId: testId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            buttonText: getButtonText(),
            buttonIcon: /*#__PURE__*/ _jsx(PaymentsLine, {}),
            secondaryButtonText: "View",
            secondaryBtnHeapId: "".concat(heapId, "-view-btn"),
            secondaryButtonIcon: /*#__PURE__*/ _jsx(EyeLine, {}),
            onClickButton: onButtonClick,
            onClickSecondaryButton: ()=>history.push(agreementLink),
            mobile: {
                title: relationshipSummaryModel.opposingPartyName(agreement, isVendor),
                subtitle: displayedTitle !== null && displayedTitle !== void 0 ? displayedTitle : undefined
            },
            children: [
                isVendor && !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(ReceiptEditLiner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-edit-agreement"),
                    onClick: ()=>onEditAgreement(agreement),
                    children: "Edit agreement"
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(Edit1Liner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-rename-agreement"),
                    onClick: ()=>onRenameAgreement(agreement, (title)=>{
                            const newAgreement = relationshipSummaryModel.setTitle(agreement, title);
                            setAgreement(newAgreement);
                        }),
                    children: "Rename agreement"
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(ProfileLiner24, {}),
                    testId: "".concat(testId, "-change-assignee"),
                    onClick: ()=>onChangeAssignee(agreement),
                    children: "Change assignee"
                }),
                isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.DuplicateAgreement,
                    iconStart: /*#__PURE__*/ _jsx(CopyLiner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-duplicate"),
                    "data-heapid": "".concat(heapId, "-menu-duplicate"),
                    onClick: ()=>onDuplicateAgreement(agreement),
                    children: "Duplicate agreement"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.DownloadAgreementPdf,
                    iconStart: /*#__PURE__*/ _jsx(Download, {}),
                    testId: "".concat(testId, "-download-pdf"),
                    "data-heapid": "".concat(heapId, "-menu-download-pdf"),
                    onClick: ()=>onDownloadPDF(agreement),
                    children: "Download PDF"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    iconStart: /*#__PURE__*/ _jsx(LinkLiner24, {}),
                    testId: "".concat(testId, "-direct-links"),
                    onClick: ()=>{
                        overlayContext.showModal({
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            openBottomSheetOnMobile: true,
                            hideBottomSheetOnDismiss: true,
                            content: (p)=>/*#__PURE__*/ _jsx(ShareAgreementLinksModal, _object_spread_props(_object_spread({}, p), {
                                    relationship: agreement
                                }))
                        });
                    },
                    children: "Share direct link"
                }),
                !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    as: Link,
                    to: routes.billingHub(agreement.id),
                    iconStart: /*#__PURE__*/ _jsx(EmptyWalletTickLiner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-billing-hub"),
                    "data-heapid": "".concat(heapId, "-menu-billing-hub"),
                    children: "Manage billing"
                }),
                !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    as: Link,
                    to: routes.invoices(agreement.id),
                    iconStart: /*#__PURE__*/ _jsx(ProposalLiner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-invoices"),
                    "data-heapid": "".concat(heapId, "-menu-invoices"),
                    children: "View invoices"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, _object_spread_props(_object_spread({
                    iconStart: /*#__PURE__*/ _jsx(ActivityLiner24, {}),
                    disabled: agreement.isSample,
                    testId: "".concat(testId, "-activity"),
                    "data-heapid": "".concat(heapId, "-menu-activity")
                }, isMobileDevice ? {
                    onClick: ()=>{
                        overlayContext.pushBottomSheetPage({
                            content: /*#__PURE__*/ _jsx(ActivityLogBottomSheetContent, {
                                agreementId: agreement.id
                            })
                        });
                    }
                } : {
                    as: Link,
                    to: routes.activityLog(agreement.id)
                }), {
                    children: "View activity log"
                })),
                isVendor && !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    intent: Intent.TerminateAgreement,
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
                    testId: "".concat(testId, "-terminate"),
                    "data-heapid": "".concat(heapId, "-menu-terminate"),
                    onClick: ()=>onTerminateAgreement(agreement),
                    children: "Terminate agreement"
                })
            ]
        })
    });
}
